import { Cheque } from '@baemingo/table-events/dist/types';
import { DateTime } from 'luxon';
import { GetIncomingBookings_operativeViewer_incomingBookingsForVenue } from '~Providers/BookingsProvider/api/__queries__';

// Calculate grid position for the current time indicator
export const getCurrentTimePosition = ({
  timeRange,
  currentTime,
}: {
  timeRange: { startHour: number; endHour: number } | null;
  currentTime: Date;
}) => {
  if (!timeRange) {
    return null;
  }

  const now = currentTime;
  const currentHours = now.getHours();
  const currentMinutes = now.getMinutes();
  const currentTotalMinutes = currentHours * 60 + currentMinutes;
  const timelineStartMinutes = timeRange.startHour * 60;
  const timelineEndMinutes = timeRange.endHour * 60 + 60; // Include the full last hour

  // Check if current time is within the visible timeline range
  if (currentTotalMinutes < timelineStartMinutes || currentTotalMinutes > timelineEndMinutes) {
    return null; // Current time is outside the visible range
  }

  // Calculate position relative to timeline start
  const minutesFromStart = currentTotalMinutes - timelineStartMinutes;
  const slotsFromStart = Math.floor(minutesFromStart / 30);
  const offsetMinutes = minutesFromStart % 30;
  const offsetPercentage = (offsetMinutes / 30) * 100;

  // Grid column starts at 2 (after the table label column)
  const gridColumn = 2 + slotsFromStart;

  return { gridColumn, offsetPercentage };
};

// Calculate grid position for a booking block based on startTime and endTime
export const getBookingPosition = ({
  timeRange,
  startTime,
  endTime,
}: {
  timeRange: { startHour: number; endHour: number } | null;
  startTime: string;
  endTime: string;
}) => {
  if (!timeRange) return { gridColumnStart: 2, gridColumnSpan: 4, offsetMinutes: 0 };

  // Parse start time
  const [startHours, startMinutes] = startTime.split(':').map(Number);
  const bookingStartMinutes = startHours * 60 + startMinutes;

  // Parse end time
  const [endHours, endMinutes] = endTime.split(':').map(Number);
  const bookingEndMinutes = endHours * 60 + endMinutes;

  const timelineStartMinutes = timeRange.startHour * 60; // Dynamic start time

  // Calculate exact position from the start (not rounded)
  const minutesFromStart = bookingStartMinutes - timelineStartMinutes;
  const exactSlotsFromStart = minutesFromStart / 30;

  // Get the base slot (floor) and the offset within that slot
  const slotsFromStart = Math.floor(exactSlotsFromStart);
  const offsetMinutes = minutesFromStart % 30; // Minutes offset within the 30-minute slot

  // Calculate duration in 30-minute slots
  const durationMinutes = bookingEndMinutes - bookingStartMinutes;
  const gridColumnSpan = Math.ceil(durationMinutes / 30);

  // Grid column starts at 2 (after the table label column)
  const gridColumnStart = 2 + slotsFromStart;

  return { gridColumnStart, gridColumnSpan, offsetMinutes };
};

// Calculate grid position for an ongoing cheque block (2-hour duration from start time)
export const getChequePosition = ({
  startTime,
  timeRange,
  currentTime,
  defaultDurationMinutes = 120,
}: {
  startTime: string;
  timeRange: { startHour: number; endHour: number } | null;
  currentTime?: Date;
  defaultDurationMinutes?: number;
}) => {
  if (!timeRange)
    return {
      gridColumnStart: 2,
      gridColumnSpan: 4,
      offsetMinutes: 0,
      isPastDue: false,
      durationMinutes: defaultDurationMinutes,
    };

  const startDate = new Date(startTime);
  const hours = startDate.getHours();
  const minutes = startDate.getMinutes();
  const chequeMinutes = hours * 60 + minutes;
  const timelineStartMinutes = timeRange.startHour * 60; // Dynamic start time

  // Calculate exact position from the start (not rounded)
  const minutesFromStart = chequeMinutes - timelineStartMinutes;
  const exactSlotsFromStart = minutesFromStart / 30;

  // Get the base slot (floor) and the offset within that slot
  const slotsFromStart = Math.floor(exactSlotsFromStart);
  const offsetMinutes = minutesFromStart % 30; // Minutes offset within the 30-minute slot

  // Grid column starts at 2 (after the table label column)
  const gridColumnStart = 2 + slotsFromStart;

  // Calculate end time (start + 2 hours)
  const endDate = new Date(startDate.getTime() + defaultDurationMinutes * 60 * 1000);

  // Check if the cheque is past due
  const isPastDue = currentTime ? endDate <= currentTime : false;

  // Calculate the actual duration in minutes
  let durationMinutes = defaultDurationMinutes;

  if (isPastDue && currentTime) {
    // If past due, extend the duration to current time
    const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes();
    durationMinutes = currentMinutes - chequeMinutes;
  }

  // Still return gridColumnSpan for backward compatibility, but also return durationMinutes
  const gridColumnSpan = Math.ceil(durationMinutes / 30);

  return { gridColumnStart, gridColumnSpan, offsetMinutes, isPastDue, durationMinutes };
};

// Format date display - show "Today", "Tomorrow", or formatted date
export const formatDateDisplay = (
  booking: GetIncomingBookings_operativeViewer_incomingBookingsForVenue,
) => {
  const now = new Date();
  // Create date strings in local timezone to avoid UTC conversion issues
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;

  const tomorrowDate = new Date(now);
  tomorrowDate.setDate(tomorrowDate.getDate() + 1);
  const tomorrow = `${tomorrowDate.getFullYear()}-${String(tomorrowDate.getMonth() + 1).padStart(
    2,
    '0',
  )}-${String(tomorrowDate.getDate()).padStart(2, '0')}`;

  const bookingDate = booking.date;

  if (bookingDate === today) return 'Today';
  if (bookingDate === tomorrow) return 'Tomorrow';

  // Parse the date string manually to avoid timezone issues
  const [year, month, day] = booking.date.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

export const scrollToCurrentTime = (
  timelineContainerRef: React.RefObject<HTMLDivElement>,
  currentTimePosition: {
    gridColumn: number;
    offsetPercentage: number;
  },
) => {
  if (timelineContainerRef.current) {
    const container = timelineContainerRef.current;

    // Calculate the pixel position of the current time
    // The current time position is calculated based on grid columns (61px each) and offset
    const currentTimePixelPosition =
      64 +
      (currentTimePosition.gridColumn - 2) * 61 +
      (currentTimePosition.offsetPercentage * 61) / 100;

    // Calculate scroll position to center the current time in the viewport
    const containerWidth = container.clientWidth;
    const scrollLeft = Math.max(0, currentTimePixelPosition - containerWidth / 2);

    // Scroll smoothly to center the current time
    container.scrollTo({
      left: scrollLeft,
      behavior: 'smooth',
    });
  }
};

export const calculateDynamicTimeRange = ({
  acceptedBookings,
  incomingBookings,
  ongoingChequesForDate,
}: {
  acceptedBookings: GetIncomingBookings_operativeViewer_incomingBookingsForVenue[];
  incomingBookings?: GetIncomingBookings_operativeViewer_incomingBookingsForVenue[];
  ongoingChequesForDate: Cheque[];
}) => {
  const times: number[] = [];

  // Get start and end times from accepted bookings
  acceptedBookings.forEach((booking) => {
    const [startH, startM] = booking.startTime.split(':').map(Number);
    times.push(startH * 60 + startM);
    if (booking.endTime) {
      const [endH, endM] = booking.endTime.split(':').map(Number);
      times.push(endH * 60 + endM);
    }
  });

  // Get start and end times from incoming bookings
  if (incomingBookings) {
    incomingBookings.forEach((booking) => {
      const [startH, startM] = booking.startTime.split(':').map(Number);
      times.push(startH * 60 + startM);
      if (booking.endTime) {
        const [endH, endM] = booking.endTime.split(':').map(Number);
        times.push(endH * 60 + endM);
      }
    });
  }

  // Get times from ongoing cheques (only if today)
  ongoingChequesForDate.forEach((cheque) => {
    if (cheque.activeAt) {
      const startDate = new Date(cheque.activeAt);
      const hours = startDate.getHours();
      const minutes = startDate.getMinutes();
      times.push(hours * 60 + minutes);
    }
  });

  if (times.length === 0) {
    return null; // No bookings or cheques
  }

  const minTime = Math.min(...times);
  const maxTime = Math.max(...times);

  // Add 1 hour (60 minutes) buffer before first booking
  const startHour = Math.max(0, Math.floor(minTime / 60) - 1);

  // Add 1 hour buffer after latest time (now includes end times)
  const endHour = Math.min(23, Math.floor(maxTime / 60) + 1);

  return { startHour, endHour };
};

export const isToday = (dateString: string) => {
  const today = DateTime.local().startOf('day');
  const date = DateTime.fromISO(dateString);

  return date.hasSame(today, 'day');
};

export const formatPhoneNumber = (phoneNumber: string | null | undefined) => {
  if (!phoneNumber) return null;
  // Remove all non-digit characters
  const digits = phoneNumber.replace(/\D/g, '');

  // Format Swedish local mobile numbers: 07X XXX XX XX
  if (digits.length === 10 && digits.startsWith('07')) {
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(
      8,
      10,
    )}`;
  }

  // Format Swedish international numbers: +46 7X XXX XX XX
  if (digits.length === 11 && digits.startsWith('467')) {
    const local = digits.slice(2); // remove "46"
    return `+46 ${local.slice(0, 2)} ${local.slice(2, 5)} ${local.slice(5, 7)} ${local.slice(
      7,
      9,
    )}`;
  }

  // Return original if it doesn't match expected formats
  return phoneNumber;
};
